import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { NumberInput, noTopGutterSx } from 'src/UIKit';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { TextToWinSettings } from 'src/modules/campaigns/types';
import { sectionDividerSx } from '../../helpers';

interface TextToWinSettingsFormProps {
  disabled?: boolean;
  errors: Record<string, string>;
  settings: TextToWinSettings;
  metadata: DefaultCustomField[] | undefined;
  messageTemplates: MessageTemplate[] | undefined;
  onSettingsChange: <K extends keyof TextToWinSettings>(
    name: K,
    value: TextToWinSettings[K],
  ) => void;
}

const getDateTimeValue = (date?: string | null): Dayjs | null => {
  if (!date) {
    return null;
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed : null;
};

type DateFieldKey = 'dateStart' | 'dateEnd' | 'dateDraw' | 'dateResultsSend';
type MessageFieldKey =
  | 'entrySuccessMessageText'
  | 'entryIntervalLimitMessageText'
  | 'entryTotalLimitMessageText'
  | 'entryNotStartedMessageText'
  | 'entryEndedMessageText'
  | 'winnerMessageText'
  | 'nonWinnerMessageText';

const messageFields: { key: MessageFieldKey; label: string; required: boolean }[] = [
  { key: 'entrySuccessMessageText', label: 'Entry success message', required: true },
  { key: 'entryIntervalLimitMessageText', label: 'Entry interval limit message', required: false },
  { key: 'entryTotalLimitMessageText', label: 'Entry total limit message', required: false },
  { key: 'entryNotStartedMessageText', label: 'Entry not started message', required: false },
  { key: 'entryEndedMessageText', label: 'Entry ended message', required: false },
  { key: 'winnerMessageText', label: 'Winner message', required: true },
  { key: 'nonWinnerMessageText', label: 'Non-winner message', required: false },
];

export const TextToWinSettingsForm: React.FC<TextToWinSettingsFormProps> = ({
  disabled = false,
  errors,
  settings,
  metadata,
  messageTemplates,
  onSettingsChange,
}) => {
  const startDate = getDateTimeValue(settings.dateStart);
  const endDate = getDateTimeValue(settings.dateEnd);
  const drawDate = getDateTimeValue(settings.dateDraw);

  const handleDateChange = (key: DateFieldKey) => (value: Dayjs | null) => {
    const nextValue = value && value.isValid() ? value.toISOString() : '';
    onSettingsChange(key, nextValue);
  };

  const renderDatePicker = (
    key: DateFieldKey,
    label: string,
    minDateTime?: Dayjs | null,
  ) => (
    <FormControl fullWidth error={!!errors[`textToWinSettings.${key}`]}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          label={label}
          ampm
          format="MM/DD/YYYY hh:mm A"
          closeOnSelect={false}
          disabled={disabled}
          value={getDateTimeValue(settings[key])}
          minDateTime={minDateTime && minDateTime.isValid() ? minDateTime : undefined}
          onChange={handleDateChange(key)}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!errors[`textToWinSettings.${key}`],
            },
          }}
        />
      </LocalizationProvider>
      <FormHelperText>{errors[`textToWinSettings.${key}`] || ' '}</FormHelperText>
    </FormControl>
  );

  const renderNumberInput = (
    key: 'entryIntervalMinutes' | 'maxEntriesPerInterval' | 'maxEntriesPerContact' | 'winnersCount',
    label: string,
    allowZero: boolean,
  ) => (
    <NumberInput
      label={label}
      margin="none"
      withThousandSeparator={false}
      value={settings[key]}
      onChange={values => {
        onSettingsChange(
          key,
          typeof values.floatValue === 'number' ? values.floatValue : null,
        );
      }}
      required
      disabled={disabled}
      error={!!errors[`textToWinSettings.${key}`]}
      helperText={errors[`textToWinSettings.${key}`] || ' '}
      withoutOnBlurVoid={false}
      allowNegative={false}
      allowZero={allowZero}
    />
  );

  return (
    <Box mb={2.5}>
      <Divider sx={sectionDividerSx}>Contest Settings</Divider>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {renderDatePicker('dateStart', 'Start Date')}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderDatePicker('dateEnd', 'End Date', startDate)}
        </Grid>
        <Grid item xs={12} md={6} sx={noTopGutterSx}>
          {renderDatePicker('dateDraw', 'Draw Date', endDate)}
        </Grid>
        <Grid item xs={12} md={6} sx={noTopGutterSx}>
          {renderDatePicker('dateResultsSend', 'Results Send Date', drawDate)}
        </Grid>
        <Grid item xs={12} md={6} sx={noTopGutterSx}>
          {renderNumberInput('entryIntervalMinutes', 'Entry Interval Minutes', false)}
        </Grid>
        <Grid item xs={12} md={6} sx={noTopGutterSx}>
          {renderNumberInput('maxEntriesPerInterval', 'Max Entries Per Interval', false)}
        </Grid>
        <Grid item xs={12} md={6} sx={noTopGutterSx}>
          {renderNumberInput('maxEntriesPerContact', 'Max Entries Per Contact', false)}
        </Grid>
        <Grid item xs={12} md={6} sx={noTopGutterSx}>
          {renderNumberInput('winnersCount', 'Winners Count', false)}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }}>Messages</Divider>
      {messageFields.map(({ key, label, required }) => {
        const errorKey = `textToWinSettings.${key}`;
        return (
          <Box key={key} mb={2}>
            <FormLabel
              required={required}
              error={!!errors[errorKey]}
              sx={{ display: 'block', mb: 1 }}
            >
              {label}
            </FormLabel>
            <Box sx={{ position: 'relative', pointerEvents: disabled ? 'none' : 'all' }}>
              <TemplateText
                showConfirmationText={false}
                confirmationText=""
                setFocus={false}
                disabled={disabled}
                customFields={metadata || []}
                messageTemplates={messageTemplates}
                text={settings[key] || ''}
                updateText={newText => onSettingsChange(key, newText)}
                hasError={!!errors[errorKey]}
              />
            </Box>
            <FormHelperText error={!!errors[errorKey]}>
              {errors[errorKey] || ' '}
            </FormHelperText>
          </Box>
        );
      })}
    </Box>
  );
};
